import React from 'react';

import Lozenge from '@atlaskit/lozenge/lozenge';
import type { SemanticColor } from '@atlaskit/lozenge/types';
import { statusCategoryToThemeMap } from '@atlassian/search-client/hydration/statusCategoryToThemeMap';
import {
	JiraStatusCategoryColor,
	type JiraStatusCategoryColorType,
} from '@atlassian/search-client/hydration/types';

import { MetaDataItemSkeleton } from '../search-result-skeleton';

const jiraIssueStatusToLozengeMap = {
	[JiraStatusCategoryColor.Red]: 'danger',
	[JiraStatusCategoryColor.Brown]: 'neutral',
	[JiraStatusCategoryColor.Green]: 'success',
	[JiraStatusCategoryColor.Yellow]: 'warning',
	[JiraStatusCategoryColor.BlueGray]: 'information',
	[JiraStatusCategoryColor.MediumGray]: 'neutral',
} satisfies Record<JiraStatusCategoryColorType, SemanticColor>;

const STATUS_REGEX = /[\s-]+/g;
/**
 * Remove when visual refresh flag is removed
 */
const getAppearance = (name: string) => {
	name = name.replace(STATUS_REGEX, '').toLowerCase();
	switch (true) {
		case ['todo', 'unavailable', 'minor', 'notstarted'].includes(name):
			return 'neutral';

		case ['available', 'completed', 'done', 'approved', 'resolved', 'added', 'closed'].includes(
			name,
		):
			return 'success';

		case ['error', 'declined', 'deleted', 'failed', 'rejected'].includes(name):
			return 'danger';

		case ['inprogress', 'open', 'modified'].includes(name):
			return 'information';
	}
	return 'neutral';
};

export const getStatus = (
	name?: string,
	statusInfo?: { color?: JiraStatusCategoryColorType; statusCategoryId?: string },
	isLoading?: boolean,
): React.JSX.Element | null => {
	if (name) {
		if (isLoading) {
			return <MetaDataItemSkeleton />;
		}
		let appearance: SemanticColor | undefined;
		if (statusInfo?.statusCategoryId) {
			appearance = statusCategoryToThemeMap[statusInfo.statusCategoryId];
		} else if (statusInfo?.color) {
			appearance = jiraIssueStatusToLozengeMap[statusInfo.color];
		}
		if (appearance) {
			return (
				<Lozenge isBold appearance={appearance} testId="jira-status-lozenge-test-id">
					{name}
				</Lozenge>
			);
		}

		const fallbackAppearance = getAppearance(name);
		if (fallbackAppearance) {
			return (
				<Lozenge appearance={fallbackAppearance} testId="jira-status-lozenge-test-id">
					{name}
				</Lozenge>
			);
		}
	}
	if (isLoading) {
		return <MetaDataItemSkeleton />;
	}
	return null;
};
